/**
 * My Account Page Component
 */

'use client';

import styled from '@emotion/styled';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, ShoppingBag, Package, RotateCcw, MapPin, Users,
  DollarSign, Ticket, HelpCircle, MessageSquare, Headphones, LogOut,
  ChevronDown, Coins,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@template-project/afrodita-components';
import { useIntl } from '@/lib/intl';
import { authService, UserProfile } from '@/lib/services/auth.service';
import { shippingAddressService, ShippingAddress, CreateShippingAddressInput } from '@/lib/services/shipping-address.service';
import { getOrderService } from '@/infrastructure/services/order.service';
import {
  getUserCreditBalance,
  getUserCreditTransactions,
  CreditsBalance,
  CreditTransaction,
} from '@/infrastructure/services/bff/credits.bff-service';
import {
  getUserCoupons,
  CustomerCoupons,
} from '@/infrastructure/services/bff/coupons.bff-service';
import {
  Order, Address, CurrencyAmount, Refund,
  RefundType, RefundReasonType, RefundLineItem,
} from '@/domain/entities/order.entity';
import { PurchaseHistory, OrderDetail, OrderCard as OrderCardComponent, TrackMyOrder } from '@/components/orders';
import { MembershipDashboard, AutoshipDashboard } from '@/components/subscriptions';
import { storefrontLayoutLogger } from '@/lib/logger';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.lg};
  }
`;

const Breadcrumb = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.space.xl};
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${({ theme }) => theme.space['3xl']};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: none;
  }
`;

const MobileMenuWrapper = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    display: block;
    margin-bottom: ${({ theme }) => theme.space.lg};
  }
`;

const MobileMenuButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  font-family: inherit;
`;

const MobileMenuIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MobileMenuChevron = styled.span<{ open?: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MobileMenuDropdown = styled.ul<{ open?: boolean }>`
  display: ${({ open }) => (open ? 'block' : 'none')};
  list-style: none;
  padding: 0;
  margin: ${({ theme }) => theme.space.xs} 0 0;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  overflow: hidden;
`;

const MobileMenuItem = styled.li``;

const MobileMenuLink = styled.button<{ active?: boolean }>`
  width: 100%;
  text-align: left;
  background: ${({ active, theme }) =>
    active ? theme.colors.background.secondary : 'transparent'};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ active, theme }) =>
    active ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  font-family: inherit;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const UserGreeting = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const GreetingText = styled.div`
  flex: 1;
  
  h3 {
    font-size: ${({ theme }) => theme.fontSizes[2]}px;
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li<{ active?: boolean }>`
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const MenuLink = styled.button<{ active?: boolean }>`
  width: 100%;
  text-align: left;
  background: ${({ active, theme }) =>
    active ? theme.colors.background.secondary : 'transparent'};
  border: none;
  border-left: 3px solid
    ${({ active, theme }) => (active ? theme.colors.text.primary : 'transparent')};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ active, theme }) =>
    active ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  transition: all 0.2s;
  font-family: inherit;
  
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MainContent = styled.div``;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes[6]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    font-size: ${({ theme }) => theme.fontSizes[4]}px;
  }
`;

const SeeAllLink = styled.a`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  
  &:hover {
    text-decoration: none;
  }
`;

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.space['3xl']};
`;

const OrderCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const OrderSeparator = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.border.color};
  margin: ${({ theme }) => theme.space.lg} 0;
  width: 100%;
`;

const OrderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.lg};
  padding-bottom: ${({ theme }) => theme.space.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  flex-wrap: wrap;
`;

const OrderNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const CopyIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const OrderStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const OrderContent = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: ${({ theme }) => theme.space.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: auto 1fr;
    gap: ${({ theme }) => theme.space.md};
  }
`;

const OrderImage = styled.img`
  width: 80px;
  height: 100px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    width: 60px;
    height: 75px;
  }
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.sm};
  }
`;

const OrderDetailItem = styled.div`
  h4 {
    font-size: ${({ theme }) => theme.fontSizes[1]}px;
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 0 0 ${({ theme }) => theme.space.xs} 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes[2]}px;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
  }
`;

const ViewDetailsButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${({ theme }) => theme.colors.text.primary};
    color: ${({ theme }) => theme.colors.background.primary};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    width: 100%;
    text-align: center;
    grid-column: 1 / -1;
  }
`;

const AddressCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const AddressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const AddressName = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const DefaultBadge = styled.span`
  background: rgba(47, 128, 106, 0.1);
  color: ${({ theme }) => theme.colors.semantic.positive.color};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AddressContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const DefaultAddressToggle = styled.label<{ isDefault: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  background: ${({ isDefault, theme }) => 
    isDefault ? 'rgba(47, 128, 106, 0.08)' : theme.colors.background.secondary};
  border: 1px solid ${({ isDefault, theme }) => 
    isDefault ? theme.colors.semantic.positive.color : theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  transition: all 0.2s ease;

  &:hover {
    ${({ disabled, theme, isDefault }) => !disabled && `
      border-color: ${isDefault ? theme.colors.semantic.positive.color : theme.colors.text.secondary};
    `}
  }
`;

const DefaultRadioInput = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid ${({ theme }) => theme.colors.border.color};
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:checked {
    border-color: ${({ theme }) => theme.colors.semantic.positive.color};
    background: ${({ theme }) => theme.colors.semantic.positive.color};
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DefaultRadioText = styled.span<{ isDefault: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme, isDefault }) => 
    isDefault ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};
  color: ${({ theme, isDefault }) => 
    isDefault ? theme.colors.semantic.positive.color : theme.colors.text.secondary};
`;

const AddAddressButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px dashed ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  
  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Empty State Components for Shipping Addresses
const EmptyStateCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space['3xl']} ${({ theme }) => theme.space.xl};
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.space.lg} 0;
`;

const AddNewAddressButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    opacity: 0.9;
  }
`;

const PageTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.space.xl} 0;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    font-size: ${({ theme }) => theme.fontSizes[3]}px;
    margin: 0 0 ${({ theme }) => theme.space.lg} 0;
  }
`;

// Form Components for Add New Shipping Address
const FormHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    opacity: 0.7;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const FormTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.lg};
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
`;

const FormInput = styled.input`
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const FormSelect = styled.select`
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  margin-top: ${({ theme }) => theme.space.lg};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const SubmitButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormError = styled.p`
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  margin: 0;
`;

// Refund UI Styled Components
const RefundContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;

const RefundOrderSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RefundModeGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};

  @media (max-width: ${({ theme }) => theme.mediaQueries.xs}) {
    flex-direction: column;
  }
`;

const RefundModeButton = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.border.color};
  background: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.background.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.background.primary : theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  font-family: inherit;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RefundLineItemRow = styled.label<{ disabled?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: ${({ theme }) => theme.space.md};
  align-items: center;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: background 0.15s;

  &:hover {
    background: ${({ disabled, theme }) =>
      disabled ? 'transparent' : theme.colors.background.secondary};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: auto 1fr auto;
    gap: ${({ theme }) => theme.space.sm};
  }
`;

const RefundCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: inherit;
`;

const RefundItemImage = styled.img`
  width: 48px;
  height: 60px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
`;

const RefundItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RefundItemName = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RefundItemMeta = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const QuantityInput = styled.input`
  width: 60px;
  padding: ${({ theme }) => theme.space.sm};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-family: inherit;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AmountInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefundReasonSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RefundReasonTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${({ theme }) => theme.space.md};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const RefundSubmitButton = styled.button`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space['2xl']};
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-start;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefundFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const RefundFieldLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RefundHelpText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const RefundHistoryCard = styled(Card)`
  margin-top: ${({ theme }) => theme.space.lg};
`;

const RefundHistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: ${({ theme }) => theme.space.md};
  align-items: center;
  padding: ${({ theme }) => theme.space.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr auto;
    gap: ${({ theme }) => theme.space.sm};
  }
`;

const RefundStatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ status }) =>
    status === 'completed'
      ? 'rgba(47, 128, 106, 0.1)'
      : status === 'failed'
        ? 'rgba(197, 48, 48, 0.1)'
        : 'rgba(200, 170, 50, 0.1)'};
  color: ${({ status }) =>
    status === 'completed'
      ? '#2f806a'
      : status === 'failed'
        ? '#c53030'
        : '#a08520'};
`;

const RefundAlert = styled.div<{ variant: 'success' | 'error' }>`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  background: ${({ variant }) =>
    variant === 'success' ? 'rgba(47, 128, 106, 0.1)' : 'rgba(197, 48, 48, 0.1)'};
  color: ${({ variant }) => (variant === 'success' ? '#2f806a' : '#c53030')};
  border: 1px solid ${({ variant }) => (variant === 'success' ? '#2f806a' : '#c53030')};
`;

const ShippingRefundInfo = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.sm};
`;

const REFUND_REASON_OPTIONS: { value: RefundReasonType; label: string }[] = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'defective_product', label: 'Defective Product' },
  { value: 'wrong_item', label: 'Wrong Item Received' },
  { value: 'item_not_received', label: 'Item Not Received' },
  { value: 'duplicate_order', label: 'Duplicate Order' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'shipping_issue', label: 'Shipping Issue' },
  { value: 'other', label: 'Other' },
];

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Helper functions for formatting order data
const formatOrderDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const formatted = date.toLocaleDateString('en-US', options);
    // Add ordinal suffix (1st, 2nd, 3rd, etc.)
    const day = date.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' :
                   day % 10 === 2 && day !== 12 ? 'nd' :
                   day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return formatted.replace(/\d+/, `${day}${suffix}`);
  } catch (error) {
    return dateString;
  }
};

const formatCurrency = (
  amount: number,
  locale: string = 'en-US',
  currencyCode: string = 'USD',
  symbolOverride?: string
): string => {
  const dollars = amount / 100;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

  if (!symbolOverride) {
    return formatter.format(dollars);
  }

  return formatter
    .formatToParts(dollars)
    .map((part) => (part.type === 'currency' ? symbolOverride : part.value))
    .join('');
};

const formatShippingAddress = (address: Address): string => {
  const parts = [
    address.address1,
    address.address2,
    address.city,
    address.state,
    address.zip,
    address.country,
  ].filter(Boolean);
  return parts.join(', ');
};

const getOrderImage = (order: Order): string => {
  if (order.line_items && order.line_items.length > 0 && order.line_items[0].image_url) {
    return order.line_items[0].image_url;
  }
  return 'https://cdn.shapermint.com/assets/shapermint/images/SHM_Account_Login-img.jpg';
};

const getOrderTotalQuantity = (order: Order): number => {
  if (!order.line_items || order.line_items.length === 0) {
    return 0;
  }
  return order.line_items.reduce((total, item) => total + (item.quantity || 0), 0);
};


export function MyAccountPage() {
  const navigate = useNavigate();
  const { countryConfig } = useIntl();
  const [activeMenuItem, setActiveMenuItem] = useState('account-overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Shipping Address Form State
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateShippingAddressInput>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: 'Alabama',
    country: 'United States',
    zipCode: '',
    phone: '',
    isDefault: false,
  });

  // My Credits state
  const [creditsBalance, setCreditsBalance] = useState<CreditsBalance | null>(null);
  const [creditsTransactions, setCreditsTransactions] = useState<CreditTransaction[]>([]);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);

  // Coupons state
  const [couponData, setCouponData] = useState<CustomerCoupons | null>(null);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Check authentication and redirect if not logged in
  useEffect(() => {
    const checkAuth = async () => {
      // Check if user has a session marker in localStorage
      const hasSessionMarker = authService.hasSession();
      
      // If no session marker at all, user never logged in
      if (!hasSessionMarker) {
        navigate('/login', { replace: true });
        return;
      }

      // Try to restore session if needed (after page refresh)
      // The access token is in memory only, so after refresh we need to get a new one
      if (!authService.isAuthenticated()) {
        const result = await authService.tryRestoreSession();
        
        if (!result.success) {
          if (result.authError) {
            // Auth error (401/403) - refresh token is invalid/expired
            // Clear session and redirect to login
            authService.clearSession();
          navigate('/login', { replace: true });
            return;
          }
          // Network/server error - don't clear session, don't redirect
          // Show error message and allow retry
          setAuthError('Could not connect to authentication server. Please check your connection and try again.');
          setIsLoading(false);
          return;
        }
      }

      // We have a valid access token, fetch user profile
      try {
        const userData = await authService.fetchCurrentUser();
        if (!userData) {
          authService.clearSession();
          navigate('/login', { replace: true });
          return;
        }
        setUser(userData);
        setAuthError(null);
      } catch (error) {
        storefrontLayoutLogger.error('Failed to load current user profile', error instanceof Error ? error : undefined);
        // Check if it's an auth error or network error
        const isAuthError = error instanceof Error && 
          (error.message.includes('Not authenticated') || 
           error.message.includes('401') || 
           error.message.includes('403'));
        
        if (isAuthError) {
          authService.clearSession();
        navigate('/login', { replace: true });
        } else {
          // Network error - show error message
          setAuthError('Failed to load your profile. Please check your connection and try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    await authService.logout();
    navigate('/login');
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }).catch(() => {
      storefrontLayoutLogger.error('Failed to copy coupon code to clipboard');
    });
  };

  // Reset form state when navigating away from shipping addresses
  useEffect(() => {
    if (activeMenuItem !== 'shipping-addresses') {
      setShowAddressForm(false);
      setFormError(null);
    }
    if (activeMenuItem !== 'purchases-history') {
      setSelectedOrderId(null);
    }
  }, [activeMenuItem]);

  // Fetch shipping addresses when account overview or shipping addresses section is viewed
  useEffect(() => {
    if ((activeMenuItem === 'shipping-addresses' && !showAddressForm) || activeMenuItem === 'account-overview') {
      fetchAddresses();
    }
  }, [activeMenuItem, showAddressForm]);

  // Fetch coupons data when Coupons section is opened
  useEffect(() => {
    if (activeMenuItem === 'coupons' && user) {
      setIsLoadingCoupons(true);
      getUserCoupons(user.id)
        .then((data) => setCouponData(data))
        .catch((err) => {
          storefrontLayoutLogger.error('Failed to fetch coupons', err instanceof Error ? err : undefined);
          setCouponData({ available: [], used: [] });
        })
        .finally(() => setIsLoadingCoupons(false));
    }
  }, [activeMenuItem, user]);

  // Fetch credits data when My Credits section is opened
  useEffect(() => {
    if (activeMenuItem === 'my-credits' && user) {
      setIsLoadingCredits(true);
      Promise.all([
        getUserCreditBalance(user.id),
        getUserCreditTransactions(user.id),
      ])
        .then(([balance, txResult]) => {
          setCreditsBalance(balance);
          setCreditsTransactions(txResult?.transactions ?? []);
        })
        .catch((err) => {
          storefrontLayoutLogger.error('Failed to fetch credits data', err instanceof Error ? err : undefined);
        })
        .finally(() => {
          setIsLoadingCredits(false);
        });
    }
  }, [activeMenuItem, user]);

  const fetchAddresses = async () => {
    setIsLoadingAddresses(true);
    try {
      const fetchedAddresses = await shippingAddressService.getAddresses();
      setAddresses(fetchedAddresses);
    } catch (error) {
      storefrontLayoutLogger.error('Failed to fetch shipping addresses', error instanceof Error ? error : undefined);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    try {
      await shippingAddressService.createAddress(formData);
      // Reset form and go back to list
      setFormData({
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: 'Alabama',
        country: 'United States',
        zipCode: '',
        phone: '',
        isDefault: false,
      });
      setShowAddressForm(false);
      // Refresh the addresses list
      fetchAddresses();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Failed to create address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelForm = () => {
    setShowAddressForm(false);
    setFormError(null);
    setFormData({
      firstName: '',
      lastName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: 'Alabama',
      country: 'United States',
      zipCode: '',
      phone: '',
      isDefault: false,
    });
  };

  // State for tracking which address is being updated
  const [updatingDefaultId, setUpdatingDefaultId] = useState<string | null>(null);

  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Refund state
  const queryClient = useQueryClient();
  const [refundSelectedOrderId, setRefundSelectedOrderId] = useState<string>('');
  const [refundMode, setRefundMode] = useState<RefundType>('items');
  const [refundSelectedItems, setRefundSelectedItems] = useState<Record<string, number>>({});
  const [refundCustomAmount, setRefundCustomAmount] = useState('');
  const [refundReasonType, setRefundReasonType] = useState<RefundReasonType>('customer_request');
  const [refundReasonText, setRefundReasonText] = useState('');
  const [refundAlert, setRefundAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [refundDetailOrder, setRefundDetailOrder] = useState<Order | null>(null);
  const [isLoadingRefundOrder, setIsLoadingRefundOrder] = useState(false);

  const handleSetDefaultAddress = async (addressId: string) => {
    // Don't allow setting default if already updating
    if (updatingDefaultId) return;

    // Find the address
    const address = addresses.find(a => a.id === addressId);
    if (!address || address.isDefault) return;

    setUpdatingDefaultId(addressId);
    
    try {
      await shippingAddressService.updateAddress(addressId, { isDefault: true });
      
      // Update local state - set this address as default and unset others
      setAddresses(prev => prev.map(a => ({
        ...a,
        isDefault: a.id === addressId
      })));
    } catch (error) {
      storefrontLayoutLogger.error('Failed to set default shipping address', error instanceof Error ? error : undefined, {
        addressId,
      });
      // Optionally show an error message to the user
    } finally {
      setUpdatingDefaultId(null);
    }
  };

  // Fetch orders function - always use email
  const fetchOrders = useCallback(async (limit: number = 10) => {
    if (!user || !user.email) {
      setOrdersError('Unable to fetch orders: user email not available');
      return;
    }

    setIsLoadingOrders(true);
    setOrdersError(null);

    try {
      const orderService = getOrderService();
      const response = await orderService.getOrdersByEmail(user.email, 1, limit);
      setOrders(response.data || []);
    } catch (error) {
      storefrontLayoutLogger.error('Failed to fetch account orders', error instanceof Error ? error : undefined, {
        email: user.email,
      });
      setOrdersError(error instanceof Error ? error.message : 'Failed to fetch orders');
      setOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [user]);

  // Refund mutation
  const refundMutation = useMutation({
    mutationFn: async (params: { orderId: string; body: any }) => {
      const orderService = getOrderService();
      return orderService.refundOrder(params.orderId, params.body);
    },
    onSuccess: () => {
      setRefundAlert({ type: 'success', message: 'Refund submitted successfully!' });
      setRefundSelectedItems({});
      setRefundCustomAmount('');
      setRefundReasonText('');
      // Refresh the selected order detail after a short delay to allow backend processing
      setTimeout(() => {
        if (refundSelectedOrderId) {
          fetchRefundOrder(refundSelectedOrderId);
        }
        // Also invalidate orders list
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        if (user?.email) fetchOrders(50);
      }, 5000);
    },
    onError: (error: Error) => {
      setRefundAlert({ type: 'error', message: error.message });
    },
  });

  const fetchRefundOrder = useCallback(async (orderId: string) => {
    setIsLoadingRefundOrder(true);
    try {
      const orderService = getOrderService();
      const order = await orderService.getOrderById(orderId);
      setRefundDetailOrder(order);
    } catch (error) {
      storefrontLayoutLogger.error('Failed to fetch refund order detail', error instanceof Error ? error : undefined, {
        orderId,
      });
      setRefundDetailOrder(null);
    } finally {
      setIsLoadingRefundOrder(false);
    }
  }, []);

  // When selected order changes, fetch its details
  useEffect(() => {
    if (refundSelectedOrderId) {
      fetchRefundOrder(refundSelectedOrderId);
      setRefundAlert(null);
      setRefundSelectedItems({});
      setRefundCustomAmount('');
    } else {
      setRefundDetailOrder(null);
    }
  }, [refundSelectedOrderId, fetchRefundOrder]);

  // Eligible orders: payment_status paid or partially_refunded
  const eligibleOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          o.payment_status.value === 'paid' ||
          o.payment_status.value === 'partially_refunded'
      ),
    [orders]
  );

  // Fetch orders for refunds section too
  useEffect(() => {
    if (user && activeMenuItem === 'exchanges-returns') {
      fetchOrders(50);
    }
  }, [user, activeMenuItem, fetchOrders]);

  // Refund helpers
  const getRefundableBalance = useCallback(
    (order: Order | null): CurrencyAmount => {
      if (!order) return { base: 0, display: 0 };
      const totalPaid = order.totals.total;
      const totalRefunded = order.total_refunded ?? { base: 0, display: 0 };
      return {
        base: totalPaid.base - totalRefunded.base,
        display: totalPaid.display - totalRefunded.display,
      };
    },
    []
  );

  const getItemRefundedQuantity = useCallback(
    (order: Order | null, lineItemId: string): number => {
      if (!order?.refunds) return 0;
      return order.refunds.reduce((sum, r) => {
        const item = r.line_items?.find((li) => li.line_item_id === lineItemId);
        return sum + (item?.quantity ?? 0);
      }, 0);
    },
    []
  );

  const isShippingRefunded = useCallback(
    (order: Order | null): boolean => {
      if (!order?.refunds) return false;
      return order.refunds.some((r) => r.type === 'shipping');
    },
    []
  );

  const handleRefundSubmit = () => {
    if (!refundDetailOrder) return;

    const body: any = {
      type: refundMode,
      reason_type: refundReasonType,
      reason: refundReasonText || undefined,
    };

    if (refundMode === 'items') {
      const items = Object.entries(refundSelectedItems)
        .filter(([, qty]) => qty > 0)
        .map(([lineItemId, quantity]) => ({ line_item_id: lineItemId, quantity }));
      if (items.length === 0) return;
      body.items = items;
    } else if (refundMode === 'shipping') {
      // No extra fields needed, the backend handles shipping amount
    } else if (refundMode === 'amount') {
      const cents = Math.round(parseFloat(refundCustomAmount) * 100);
      if (isNaN(cents) || cents <= 0) return;
      const ratio = parseFloat(refundDetailOrder.currency_ratio) || 1;
      body.amount = { base: Math.round(cents / ratio), display: cents };
    }

    refundMutation.mutate({ orderId: refundDetailOrder.id, body });
  };

  // Fetch orders when user is loaded and viewing relevant sections
  useEffect(() => {
    if (user && (activeMenuItem === 'account-overview' || activeMenuItem === 'purchases-history' || activeMenuItem === 'track-order')) {
      const limit = activeMenuItem === 'account-overview' ? 3 : 50;
      fetchOrders(limit);
    }
  }, [user, activeMenuItem, fetchOrders]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading...</p>
        </div>
      </PageContainer>
    );
  }

  // Show authentication error if there is one
  if (authError) {
    return (
      <PageContainer>
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 style={{ color: '#c53030', marginBottom: '16px' }}>Authentication Error</h2>
          <p style={{ 
            color: '#666', 
            marginBottom: '24px',
            padding: '16px',
            background: '#fff5f5',
            borderRadius: '8px',
            border: '1px solid #feb2b2'
          }}>
            {authError}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              onClick={() => {
                setAuthError(null);
                setIsLoading(true);
                window.location.reload();
              }}
              style={{
                padding: '10px 24px',
                background: '#4a5568',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={() => {
                authService.clearSession();
                navigate('/login', { replace: true });
              }}
              style={{
                padding: '10px 24px',
                background: '#000',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Don't render the page if no user AND no session marker
  // (If we have a session marker but no user data, show page with limited info)
  if (!user && !authService.hasSession()) {
    return null;
  }

  const menuItems = [
    { id: 'account-overview', label: 'Account Overview', icon: <User size={16} /> },
    { id: 'purchases-history', label: 'Purchases History', icon: <ShoppingBag size={16} /> },
    { id: 'track-order', label: 'Track my Order', icon: <Package size={16} /> },
    { id: 'exchanges-returns', label: 'Exchanges & Returns', icon: <RotateCcw size={16} /> },
    { id: 'shipping-addresses', label: 'Shipping Addresses', icon: <MapPin size={16} /> },
    { id: 'autoship', label: 'Autoship', icon: <RotateCcw size={16} /> },
    { id: 'manage-memberships', label: 'Manage Memberships', icon: <Users size={16} /> },
    { id: 'refer-earn', label: 'Refer & Earn', icon: <DollarSign size={16} /> },
    { id: 'my-credits', label: 'My Credits', icon: <Coins size={16} /> },
    { id: 'coupons', label: 'Coupons', icon: <Ticket size={16} /> },
    { id: 'help-center', label: 'Help Center', icon: <HelpCircle size={16} /> },
    { id: 'faqs', label: 'FAQs', icon: <MessageSquare size={16} /> },
    { id: 'customer-care', label: 'Customer Care', icon: <Headphones size={16} /> },
  ];

  const getUserDisplayName = () => {
    // First try to get from user profile
    if (user?.profile?.firstName) {
      return user.profile.firstName;
    }
    // Fall back to displayName from localStorage
    const displayName = authService.getDisplayName();
    if (displayName) {
      return displayName;
    }
    return 'User';
  };

  return (
    <PageContainer>
      <Breadcrumb>
        <a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Home</a> | My Account
      </Breadcrumb>

      <ContentGrid>
        <MobileMenuWrapper>
          <MobileMenuButton
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-expanded={mobileMenuOpen}
            aria-label="Account navigation"
          >
            <MobileMenuIcon>
              {menuItems.find((m) => m.id === activeMenuItem)?.icon}
            </MobileMenuIcon>
            {menuItems.find((m) => m.id === activeMenuItem)?.label ?? 'Menu'}
            <MobileMenuChevron open={mobileMenuOpen}>
              <ChevronDown size={18} />
            </MobileMenuChevron>
          </MobileMenuButton>
          <MobileMenuDropdown open={mobileMenuOpen}>
            {menuItems.map((item) => (
              <MobileMenuItem key={item.id}>
                <MobileMenuLink
                  active={activeMenuItem === item.id}
                  onClick={() => {
                    setActiveMenuItem(item.id);
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.icon}
                  {item.label}
                </MobileMenuLink>
              </MobileMenuItem>
            ))}
            <MobileMenuItem>
              <MobileMenuLink onClick={handleSignOut}>
                <LogOut size={18} />
                Sign out
              </MobileMenuLink>
            </MobileMenuItem>
          </MobileMenuDropdown>
        </MobileMenuWrapper>

        <Sidebar>
          <UserGreeting>
            <Avatar><User size={24} /></Avatar>
            <GreetingText>
              <h3>Hi {getUserDisplayName()}</h3>
            </GreetingText>
            <EditButton aria-label="Edit profile">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </EditButton>
          </UserGreeting>

          <MenuList>
            {menuItems.map((item) => (
              <MenuItem key={item.id}>
                <MenuLink
                  active={activeMenuItem === item.id}
                  onClick={() => setActiveMenuItem(item.id)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </MenuLink>
              </MenuItem>
            ))}
            <MenuItem>
              <MenuLink onClick={handleSignOut}>
                <span><LogOut size={16} /></span>
                Sign out
              </MenuLink>
            </MenuItem>
          </MenuList>
        </Sidebar>

        <MainContent>
          {/* Account Overview - Default View */}
          {activeMenuItem === 'account-overview' && (
            <>
              <SectionHeader>
                <SectionTitle>Account Overview</SectionTitle>
              </SectionHeader>

              {/* My Purchases Section */}
              <Section>
                <SectionHeader>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>My Purchases</h3>
                  <SeeAllLink onClick={() => setActiveMenuItem('purchases-history')} style={{ cursor: 'pointer' }}>See All</SeeAllLink>
                </SectionHeader>

                {isLoadingOrders ? (
                  <p style={{ textAlign: 'center', padding: '20px' }}>Loading orders...</p>
                ) : ordersError ? (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#c53030' }}>{ordersError}</p>
                ) : orders.length > 0 ? (
                  <OrderCardComponent
                    order={orders[0]}
                    locale={countryConfig.locale}
                    currencyCode={countryConfig.currency.code}
                    currencySymbol={countryConfig.currency.format}
                    onViewDetails={(id) => {
                      setSelectedOrderId(id);
                      setActiveMenuItem('purchases-history');
                    }}
                  />
                ) : (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    You haven&apos;t made any purchases yet.
                  </p>
                )}
              </Section>

              {/* Shipping Addresses Preview Section */}
              <Section>
                <SectionHeader>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
                    Shipping addresses
                  </h3>
                  <SeeAllLink onClick={() => setActiveMenuItem('shipping-addresses')} style={{ cursor: 'pointer' }}>See All</SeeAllLink>
                </SectionHeader>

                {isLoadingAddresses ? (
                  <AddressCard>
                    <AddressContent>Loading...</AddressContent>
                  </AddressCard>
                ) : addresses.length > 0 ? (
                  /* Show only the default address (or first one) */
                  (() => {
                    const defaultAddress = addresses.find(a => a.isDefault) || addresses[0];
                    return (
                      <AddressCard key={defaultAddress.id}>
                        <AddressHeader>
                          <AddressName>
                            {defaultAddress.firstName.toUpperCase()} {defaultAddress.lastName.toUpperCase()}
                            {defaultAddress.isDefault && <DefaultBadge>DEFAULT</DefaultBadge>}
                          </AddressName>
                        </AddressHeader>
                        <AddressContent>
                          {defaultAddress.addressLine1}
                          {defaultAddress.addressLine2 && <><br />{defaultAddress.addressLine2}</>}
                          <br />
                          {defaultAddress.zipCode}
                          <br />
                          {defaultAddress.city} {defaultAddress.state}
                          <br />
                          {defaultAddress.country}
                          {defaultAddress.phone && <><br />Phone number: {defaultAddress.phone}</>}
                        </AddressContent>
                      </AddressCard>
                    );
                  })()
                ) : (
                  <AddressCard>
                    <AddressContent style={{ textAlign: 'center', color: '#666' }}>
                      No shipping addresses saved yet.
                    </AddressContent>
                  </AddressCard>
                )}
              </Section>
            </>
          )}

          {/* Shipping Addresses - Dedicated Page */}
          {activeMenuItem === 'shipping-addresses' && (
            <>
              {showAddressForm ? (
                /* Add New Address Form */
                <>
                  <FormHeader>
                    <BackButton onClick={handleCancelForm} aria-label="Go back">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                    </BackButton>
                    <FormTitle>Add New Shipping Address</FormTitle>
                  </FormHeader>

                  <FormContainer onSubmit={handleAddressSubmit}>
                    <FormRow>
                      <FormField>
                        <FormLabel>First Name</FormLabel>
                        <FormInput
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleFormChange}
                          placeholder=""
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Last Name</FormLabel>
                        <FormInput
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleFormChange}
                          placeholder=""
                          required
                        />
                      </FormField>
                    </FormRow>

                    <FormField>
                      <FormLabel>Address 1 <RequiredMark>*</RequiredMark></FormLabel>
                      <FormInput
                        type="text"
                        name="addressLine1"
                        value={formData.addressLine1}
                        onChange={handleFormChange}
                        placeholder=""
                        required
                      />
                    </FormField>

                    <FormField>
                      <FormLabel>Apt, suite, etc. (optional)</FormLabel>
                      <FormInput
                        type="text"
                        name="addressLine2"
                        value={formData.addressLine2}
                        onChange={handleFormChange}
                        placeholder=""
                      />
                    </FormField>

                    <FormRow>
                      <FormField>
                        <FormLabel>City <RequiredMark>*</RequiredMark></FormLabel>
                        <FormInput
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleFormChange}
                          placeholder=""
                          required
                        />
                      </FormField>
                      <FormField>
                        <FormLabel>Zip Code <RequiredMark>*</RequiredMark></FormLabel>
                        <FormInput
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleFormChange}
                          placeholder=""
                          required
                        />
                      </FormField>
                    </FormRow>

                    <FormRow>
                      <FormField>
                        <FormLabel>State</FormLabel>
                        <FormSelect
                          name="state"
                          value={formData.state}
                          onChange={handleFormChange}
                        >
                          {US_STATES.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </FormSelect>
                      </FormField>
                      <FormField>
                        <FormLabel>Country</FormLabel>
                        <FormSelect
                          name="country"
                          value={formData.country}
                          onChange={handleFormChange}
                        >
                          <option value="United States">United States</option>
                        </FormSelect>
                      </FormField>
                    </FormRow>

                    <FormField>
                      <FormLabel>Phone</FormLabel>
                      <FormInput
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleFormChange}
                        placeholder=""
                      />
                    </FormField>

                    {formError && <FormError>{formError}</FormError>}

                    <FormButtons>
                      <CancelButton type="button" onClick={handleCancelForm}>
                        CANCEL
                      </CancelButton>
                      <SubmitButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'ADDING...' : 'ADD'}
                      </SubmitButton>
                    </FormButtons>
                  </FormContainer>
                </>
              ) : (
                /* Shipping Addresses List */
                <>
                  <PageTitle>Shipping Address</PageTitle>
                  
                  {isLoadingAddresses ? (
                    <EmptyStateCard>
                      <EmptyStateText>Loading addresses...</EmptyStateText>
                    </EmptyStateCard>
                  ) : addresses.length === 0 ? (
                    <EmptyStateCard>
                      <EmptyStateText>
                        You currently have no saved addresses. Get started by adding one!
                      </EmptyStateText>
                      <AddNewAddressButton onClick={() => setShowAddressForm(true)}>
                        ADD NEW SHIPPING ADDRESS
                      </AddNewAddressButton>
                    </EmptyStateCard>
                  ) : (
                    <>
                      {addresses.map((address) => (
                        <AddressCard key={address.id}>
                          <AddressHeader>
                            <AddressName>
                              {address.firstName.toUpperCase()} {address.lastName.toUpperCase()}
                              {address.isDefault && <DefaultBadge>DEFAULT</DefaultBadge>}
                            </AddressName>
                          </AddressHeader>
                          <AddressContent>
                            {address.addressLine1}
                            {address.addressLine2 && <><br />{address.addressLine2}</>}
                            <br />
                            {address.zipCode}
                            <br />
                            {address.city} {address.state}
                            <br />
                            {address.country}
                            {address.phone && <><br />Phone number: {address.phone}</>}
                          </AddressContent>
                          <DefaultAddressToggle 
                            isDefault={address.isDefault}
                            disabled={updatingDefaultId !== null}
                            onClick={() => !address.isDefault && handleSetDefaultAddress(address.id)}
                          >
                            <DefaultRadioInput
                              type="radio"
                              name="defaultAddress"
                              checked={address.isDefault}
                              onChange={() => handleSetDefaultAddress(address.id)}
                              disabled={address.isDefault || updatingDefaultId !== null}
                            />
                            <DefaultRadioText isDefault={address.isDefault}>
                              {(() => {
                                if (updatingDefaultId === address.id) return 'Setting as default...';
                                if (address.isDefault) return 'This is your default shipping address';
                                return 'Set as default shipping address';
                              })()}
                            </DefaultRadioText>
                          </DefaultAddressToggle>
                        </AddressCard>
                      ))}

                      <AddAddressButton onClick={() => setShowAddressForm(true)}>
                        <span style={{ fontSize: '20px' }}>+</span>
                        Add new shipping address
                      </AddAddressButton>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* Purchases History - Dedicated Page */}
          {activeMenuItem === 'purchases-history' && (
            <>
              {selectedOrderId ? (
                <OrderDetail
                  orderId={selectedOrderId}
                  initialOrder={orders.find((o) => o.id === selectedOrderId)}
                  locale={countryConfig.locale}
                  currencyCode={countryConfig.currency.code}
                  currencySymbol={countryConfig.currency.format}
                  onBack={() => setSelectedOrderId(null)}
                  onNavigateToReturns={() => setActiveMenuItem('exchanges-returns')}
                />
              ) : (
                <>
                  <PageTitle>Purchases History</PageTitle>
                  <PurchaseHistory
                    orders={orders}
                    isLoading={isLoadingOrders}
                    error={ordersError}
                    locale={countryConfig.locale}
                    currencyCode={countryConfig.currency.code}
                    currencySymbol={countryConfig.currency.format}
                    onViewDetails={setSelectedOrderId}
                    onStartShopping={() => navigate('/')}
                  />
                </>
              )}
            </>
          )}

          {/* Exchanges & Returns - Refund UI */}
          {activeMenuItem === 'exchanges-returns' && (
            <>
              <PageTitle>Exchanges &amp; Returns</PageTitle>

              {isLoadingOrders ? (
                <EmptyStateCard>
                  <EmptyStateText>Loading orders...</EmptyStateText>
                </EmptyStateCard>
              ) : eligibleOrders.length === 0 ? (
                <EmptyStateCard>
                  <EmptyStateText>No eligible orders found for a refund.</EmptyStateText>
                </EmptyStateCard>
              ) : (
                <RefundContainer>
                  {/* Order selector */}
                  <RefundFieldGroup>
                    <RefundFieldLabel>Select Order</RefundFieldLabel>
                    <RefundOrderSelect
                      value={refundSelectedOrderId}
                      onChange={(e) => setRefundSelectedOrderId(e.target.value)}
                    >
                      <option value="">-- Choose an order --</option>
                      {eligibleOrders.map((o) => (
                        <option key={o.id} value={o.id}>
                          #{o.number} — {formatOrderDate(o.purchase_date)} — {formatCurrency(o.totals.total.display, countryConfig.locale, countryConfig.currency.code, countryConfig.currency.format)}
                        </option>
                      ))}
                    </RefundOrderSelect>
                  </RefundFieldGroup>

                  {isLoadingRefundOrder && (
                    <EmptyStateCard>
                      <EmptyStateText>Loading order details...</EmptyStateText>
                    </EmptyStateCard>
                  )}

                  {refundDetailOrder && !isLoadingRefundOrder && (
                    <>
                      {refundAlert && (
                        <RefundAlert variant={refundAlert.type}>{refundAlert.message}</RefundAlert>
                      )}

                      {/* Refund mode tabs */}
                      <RefundFieldGroup>
                        <RefundFieldLabel>Refund Type</RefundFieldLabel>
                        <RefundModeGroup>
                          <RefundModeButton active={refundMode === 'items'} onClick={() => setRefundMode('items')}>
                            Items
                          </RefundModeButton>
                          <RefundModeButton active={refundMode === 'shipping'} onClick={() => setRefundMode('shipping')}>
                            Shipping
                          </RefundModeButton>
                          <RefundModeButton active={refundMode === 'amount'} onClick={() => setRefundMode('amount')}>
                            Amount
                          </RefundModeButton>
                        </RefundModeGroup>
                      </RefundFieldGroup>

                      {/* ITEMS mode */}
                      {refundMode === 'items' && (
                        <RefundFieldGroup>
                          <RefundFieldLabel>Select Items to Refund</RefundFieldLabel>
                          {refundDetailOrder.line_items.map((item) => {
                            const refundedQty = getItemRefundedQuantity(refundDetailOrder, item.id);
                            const remainingQty = item.quantity - refundedQty;
                            const isFullyRefunded = remainingQty <= 0;
                            const isChecked = (refundSelectedItems[item.id] ?? 0) > 0;

                            return (
                              <RefundLineItemRow key={item.id} disabled={isFullyRefunded}>
                                <RefundCheckbox
                                  type="checkbox"
                                  disabled={isFullyRefunded}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    setRefundSelectedItems((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.checked ? Math.min(1, remainingQty) : 0,
                                    }));
                                  }}
                                />
                                <RefundItemInfo>
                                  <RefundItemName>{item.name}</RefundItemName>
                                  <RefundItemMeta>
                                    {formatCurrency(item.unit_price.display, countryConfig.locale, countryConfig.currency.code, countryConfig.currency.format)} each
                                    {isFullyRefunded
                                      ? ' — Fully refunded'
                                      : refundedQty > 0
                                        ? ` — ${refundedQty} already refunded`
                                        : ''}
                                  </RefundItemMeta>
                                </RefundItemInfo>
                                {item.image_url && (
                                  <RefundItemImage src={item.image_url} alt={item.name} />
                                )}
                                <QuantityInput
                                  type="number"
                                  min={0}
                                  max={remainingQty}
                                  disabled={isFullyRefunded || !isChecked}
                                  value={refundSelectedItems[item.id] ?? 0}
                                  onChange={(e) => {
                                    const val = Math.max(0, Math.min(remainingQty, parseInt(e.target.value) || 0));
                                    setRefundSelectedItems((prev) => ({ ...prev, [item.id]: val }));
                                  }}
                                />
                              </RefundLineItemRow>
                            );
                          })}
                        </RefundFieldGroup>
                      )}

                      {/* SHIPPING mode */}
                      {refundMode === 'shipping' && (
                        <RefundFieldGroup>
                          <RefundFieldLabel>Shipping Refund</RefundFieldLabel>
                          {isShippingRefunded(refundDetailOrder) ? (
                            <ShippingRefundInfo style={{ opacity: 0.5 }}>
                              <span>Shipping has already been refunded for this order.</span>
                            </ShippingRefundInfo>
                          ) : (
                            <ShippingRefundInfo>
                              <span>Shipping cost</span>
                              <strong>
                                {formatCurrency(
                                  refundDetailOrder.totals.shipping.display,
                                  countryConfig.locale,
                                  countryConfig.currency.code,
                                  countryConfig.currency.format
                                )}
                              </strong>
                            </ShippingRefundInfo>
                          )}
                        </RefundFieldGroup>
                      )}

                      {/* AMOUNT mode */}
                      {refundMode === 'amount' && (
                        <RefundFieldGroup>
                          <RefundFieldLabel>Custom Amount</RefundFieldLabel>
                          <RefundHelpText>
                            Refundable balance:{' '}
                            {formatCurrency(
                              getRefundableBalance(refundDetailOrder).display,
                              countryConfig.locale,
                              countryConfig.currency.code,
                              countryConfig.currency.format
                            )}
                          </RefundHelpText>
                          <AmountInput
                            type="number"
                            step="0.01"
                            min="0"
                            max={(getRefundableBalance(refundDetailOrder).display / 100).toFixed(2)}
                            placeholder="0.00"
                            value={refundCustomAmount}
                            onChange={(e) => setRefundCustomAmount(e.target.value)}
                          />
                        </RefundFieldGroup>
                      )}

                      {/* Reason */}
                      <RefundFieldGroup>
                        <RefundFieldLabel>Reason</RefundFieldLabel>
                        <RefundReasonSelect
                          value={refundReasonType}
                          onChange={(e) => setRefundReasonType(e.target.value as RefundReasonType)}
                        >
                          {REFUND_REASON_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </RefundReasonSelect>
                      </RefundFieldGroup>

                      <RefundFieldGroup>
                        <RefundFieldLabel>Additional Details (optional)</RefundFieldLabel>
                        <RefundReasonTextarea
                          value={refundReasonText}
                          onChange={(e) => setRefundReasonText(e.target.value)}
                          placeholder="Tell us more about why you'd like a refund..."
                        />
                      </RefundFieldGroup>

                      {/* Submit */}
                      <RefundSubmitButton
                        disabled={
                          refundMutation.isPending ||
                          (refundMode === 'items' &&
                            Object.values(refundSelectedItems).every((q) => q === 0)) ||
                          (refundMode === 'shipping' && isShippingRefunded(refundDetailOrder)) ||
                          (refundMode === 'amount' &&
                            (!refundCustomAmount || parseFloat(refundCustomAmount) <= 0))
                        }
                        onClick={handleRefundSubmit}
                      >
                        {refundMutation.isPending ? 'Submitting...' : 'Submit Refund Request'}
                      </RefundSubmitButton>

                      {/* Refund History */}
                      {refundDetailOrder.refunds && refundDetailOrder.refunds.length > 0 && (
                        <RefundFieldGroup>
                          <RefundFieldLabel>Refund History</RefundFieldLabel>
                          <RefundHistoryCard>
                            {refundDetailOrder.refunds.map((refund: Refund) => (
                              <RefundHistoryRow key={refund.id}>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '14px', textTransform: 'capitalize' }}>
                                    {refund.type}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {formatOrderDate(refund.created_at)}
                                  </div>
                                </div>
                                <div style={{ fontSize: '14px' }}>
                                  {formatCurrency(
                                    refund.amount.display,
                                    countryConfig.locale,
                                    countryConfig.currency.code,
                                    countryConfig.currency.format
                                  )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>
                                  {refund.reason_type.replace(/_/g, ' ')}
                                </div>
                                <RefundStatusBadge status={refund.status}>
                                  {refund.status}
                                </RefundStatusBadge>
                              </RefundHistoryRow>
                            ))}
                          </RefundHistoryCard>
                        </RefundFieldGroup>
                      )}
                    </>
                  )}
                </RefundContainer>
              )}
            </>
          )}

          {/* Track My Order */}
          {activeMenuItem === 'track-order' && (
            <>
              <PageTitle>Track My Order</PageTitle>
              <TrackMyOrder
                orders={orders}
                isLoading={isLoadingOrders}
                error={ordersError}
                onViewOrderDetails={(id) => {
                  setSelectedOrderId(id);
                  setActiveMenuItem('purchases-history');
                }}
              />
            </>
          )}

          {/* Autoship Subscriptions */}
          {activeMenuItem === 'autoship' && user && (
            <AutoshipDashboard user={user} />
          )}

          {/* Manage Memberships */}
          {activeMenuItem === 'manage-memberships' && user && (
            <MembershipDashboard
              user={user}
              onBack={() => setActiveMenuItem('account-overview')}
            />
          )}

          {/* My Credits */}
          {activeMenuItem === 'my-credits' && (
            <>
              <SectionHeader>
                <SectionTitle>My Credits</SectionTitle>
              </SectionHeader>

              {isLoadingCredits ? (
                <p style={{ textAlign: 'center', padding: '20px' }}>Loading credits...</p>
              ) : (
                <>
                  {/* Balance Card */}
                  <Section>
                    <div style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                      borderRadius: '12px',
                      padding: '28px 32px',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                    }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Available Credits
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: '42px', fontWeight: 700, lineHeight: 1 }}>
                          ${(creditsBalance?.balance ?? 0).toFixed(2)}
                        </p>
                        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#888' }}>
                          1 credit = $1.00
                        </p>
                      </div>
                      <Coins size={48} color="#c9a84c" />
                    </div>
                  </Section>

                  {/* Transaction History */}
                  <Section>
                    <SectionHeader>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Transaction History</h3>
                    </SectionHeader>

                    {creditsTransactions.length === 0 ? (
                      <EmptyStateCard>
                        <EmptyStateText>
                          No credit transactions yet. Credits are earned through purchases and promotions.
                        </EmptyStateText>
                      </EmptyStateCard>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {creditsTransactions.map((tx) => {
                          const isCredit = tx.amount > 0;
                          const typeLabel = tx.type.charAt(0).toUpperCase() + tx.type.slice(1);
                          const date = new Date(tx.created_at).toLocaleDateString(countryConfig.locale, {
                            year: 'numeric', month: 'short', day: 'numeric',
                          });
                          return (
                            <div key={tx.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '14px 16px',
                              borderBottom: '1px solid #f0f0f0',
                              gap: '16px',
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}>{typeLabel}</p>
                                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>
                                  {date}{tx.source ? ` · ${tx.source}` : ''}
                                </p>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <p style={{
                                  margin: 0,
                                  fontWeight: 700,
                                  fontSize: '15px',
                                  color: isCredit ? '#2e7d32' : '#c62828',
                                }}>
                                  {isCredit ? '+' : ''}{tx.amount > 0 ? `$${tx.amount.toFixed(2)}` : `-$${Math.abs(tx.amount).toFixed(2)}`}
                                </p>
                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#aaa' }}>
                                  Balance: ${tx.balance_after.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Section>
                </>
              )}
            </>
          )}

          {/* Coupons */}
          {activeMenuItem === 'coupons' && (
            <>
              <SectionHeader>
                <SectionTitle>Coupons</SectionTitle>
              </SectionHeader>

              {isLoadingCoupons ? (
                <p style={{ textAlign: 'center', padding: '20px' }}>Loading coupons...</p>
              ) : (
                <>
                  {/* Available coupons */}
                  <Section>
                    <SectionHeader>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Available Coupons</h3>
                    </SectionHeader>

                    {!couponData || couponData.available.length === 0 ? (
                      <EmptyStateCard>
                        <EmptyStateText>No available coupons right now. Check back later!</EmptyStateText>
                      </EmptyStateCard>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {couponData.available.map((coupon) => (
                          <div key={coupon.id} style={{
                            border: '2px dashed #1a1a1a',
                            borderRadius: '10px',
                            padding: '16px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            background: '#fafafa',
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <span style={{
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  fontSize: '16px',
                                  letterSpacing: '0.08em',
                                  background: '#1a1a1a',
                                  color: '#fff',
                                  padding: '3px 10px',
                                  borderRadius: '4px',
                                }}>
                                  {coupon.code}
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  background: '#e6f4ea',
                                  color: '#2e7d32',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  fontWeight: 600,
                                }}>
                                  ACTIVE
                                </span>
                              </div>
                              <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#555' }}>{coupon.name}</p>
                              <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#888' }}>{coupon.discount_label}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCopyCode(coupon.code)}
                              style={{
                                flexShrink: 0,
                                padding: '8px 16px',
                                border: '1px solid #1a1a1a',
                                borderRadius: '6px',
                                background: copiedCode === coupon.code ? '#1a1a1a' : '#fff',
                                color: copiedCode === coupon.code ? '#fff' : '#1a1a1a',
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                minWidth: '80px',
                              }}
                            >
                              {copiedCode === coupon.code ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>

                  {/* Used / expired coupons */}
                  <Section>
                    <SectionHeader>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Used Coupons</h3>
                    </SectionHeader>

                    {!couponData || couponData.used.length === 0 ? (
                      <EmptyStateCard>
                        <EmptyStateText>You haven&apos;t used any coupons yet.</EmptyStateText>
                      </EmptyStateCard>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {couponData.used.map((usage) => {
                          const date = new Date(usage.applied_at).toLocaleDateString(countryConfig.locale, {
                            year: 'numeric', month: 'short', day: 'numeric',
                          });
                          const saved = (usage.discount_amount_display / 100).toFixed(2);
                          return (
                            <div key={usage.usage_id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '14px 16px',
                              borderBottom: '1px solid #f0f0f0',
                              gap: '16px',
                              opacity: 0.65,
                            }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    letterSpacing: '0.06em',
                                  }}>
                                    {usage.code}
                                  </span>
                                  <span style={{
                                    fontSize: '11px',
                                    background: '#f0f0f0',
                                    color: '#666',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                  }}>
                                    Used
                                  </span>
                                </div>
                                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>
                                  {usage.name} · Applied {date}
                                </p>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: '#2e7d32' }}>
                                  -${saved}
                                </p>
                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#aaa' }}>saved</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Section>
                </>
              )}
            </>
          )}

          {/* Other menu items - Coming Soon placeholder */}
          {!['account-overview', 'shipping-addresses', 'purchases-history', 'exchanges-returns', 'track-order', 'autoship', 'manage-memberships', 'my-credits', 'coupons'].includes(activeMenuItem) && (
            <>
              <PageTitle>{menuItems.find(item => item.id === activeMenuItem)?.label || 'Section'}</PageTitle>
              <EmptyStateCard>
                <EmptyStateText>
                  This section is coming soon!
                </EmptyStateText>
              </EmptyStateCard>
            </>
          )}
        </MainContent>
      </ContentGrid>
    </PageContainer>
  );
}
